"use client";

import { useMemo } from "react";
import Link from "next/link";
import type { CalendarVratYearResponse } from "@/lib/api/calendar";
import { currentIndiaDate, formatVedicEngineDate } from "@/lib/vedic-engine-date";

/**
 * Vrat-calendar hub table (rule 23/24). Lists every Hindu vrat & fast type with
 * the NEXT engine-computed date + a link to its dedicated page. EVERY date here
 * is the value the backend astrology engine returned for that tithi/ingress — no
 * date is hand-typed. The "next" date per vrat is derived by comparing engine
 * dates to today's IST date. Locale-aware so the en + hi hub render the SAME
 * component (no per-locale date copy to drift).
 */
type VratItem = { id: string; name_en: string; name_hi: string; date: string; month_hi: string };
type VratKey =
  | "ekadashis"
  | "purnimas"
  | "amavasyas"
  | "pradoshas"
  | "sankashtis"
  | "masik_shivratris"
  | "sankrantis";

interface VratRowDef {
  key: VratKey;
  label_en: string;
  label_hi: string;
  sub_en: string;
  sub_hi: string;
  href_en: string;
  href_hi: string;
}

const VRAT_ROWS: VratRowDef[] = [
  {
    key: "ekadashis",
    label_en: "Ekadashi",
    label_hi: "एकादशी",
    sub_en: "Vishnu vrat & paaran time",
    sub_hi: "विष्णु व्रत और पारण समय",
    href_en: "/ekadashi/",
    href_hi: "/hi/ekadashi/",
  },
  {
    key: "purnimas",
    label_en: "Purnima",
    label_hi: "पूर्णिमा",
    sub_en: "Full moon & Satyanarayan vrat",
    sub_hi: "पूर्णिमा और सत्यनारायण व्रत",
    href_en: "/purnima/",
    href_hi: "/hi/purnima/",
  },
  {
    key: "amavasyas",
    label_en: "Amavasya",
    label_hi: "अमावस्या",
    sub_en: "New moon & Pitru Tarpan",
    sub_hi: "अमावस्या और पितृ तर्पण",
    href_en: "/amavasya/",
    href_hi: "/hi/amavasya/",
  },
  {
    key: "pradoshas",
    label_en: "Pradosh Vrat",
    label_hi: "प्रदोष व्रत",
    sub_en: "Trayodashi Shiva vrat",
    sub_hi: "त्रयोदशी शिव व्रत",
    href_en: "/pradosh/",
    href_hi: "/hi/pradosh/",
  },
  {
    key: "sankashtis",
    label_en: "Sankashti Chaturthi",
    label_hi: "संकष्टी चतुर्थी",
    sub_en: "Ganesha obstacle-removal vrat",
    sub_hi: "गणेश बाधा-नाशक व्रत",
    href_en: "/sankashti-chaturthi/",
    href_hi: "/hi/sankashti-chaturthi/",
  },
  {
    key: "masik_shivratris",
    label_en: "Masik Shivratri",
    label_hi: "मासिक शिवरात्रि",
    sub_en: "Monthly Shiva vrat & night vigil",
    sub_hi: "मासिक शिव व्रत और रात्रि जागरण",
    href_en: "/masik-shivratri/",
    href_hi: "/hi/masik-shivratri/",
  },
  {
    key: "sankrantis",
    label_en: "Sankranti",
    label_hi: "संक्रांति",
    sub_en: "Solar ingress, snan & daan",
    sub_hi: "सूर्य संक्रमण, स्नान और दान",
    href_en: "/sankranti/",
    href_hi: "/hi/sankranti/",
  },
];

function nextOf(items: VratItem[] | undefined, today: string): VratItem | null {
  if (!items || items.length === 0) return null;
  const upcoming = items
    .filter((e) => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
  return upcoming[0] ?? null;
}

export default function VratCalendarHub({
  data,
  locale,
}: {
  data: CalendarVratYearResponse | null;
  locale: "en" | "hi";
}) {
  const today = currentIndiaDate();

  const rows = useMemo(() => {
    return VRAT_ROWS.map((def) => {
      const items = (data?.vratas[def.key] as VratItem[] | undefined) ?? [];
      return { def, next: nextOf(items, today), count: items.length };
    });
  }, [data, today]);

  const anyLoaded = rows.some((r) => r.count > 0);

  if (!anyLoaded) {
    return (
      <p className="text-body leading-relaxed text-text-muted">
        {locale === "hi"
          ? "सभी व्रत तिथियाँ वैदिक इंजन से तुरंत गणना की जाती हैं। अभी सूची लोड नहीं हो सकी — कृपया एक क्षण में पुनः लोड करें।"
          : "Every vrat date is computed live from the Vedic engine. The calendar could not be loaded right now — please refresh in a moment."}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border-default">
      <table className="w-full border-collapse text-left text-body-small">
        <thead>
          <tr className="bg-surface-brand-subtle">
            <th className="px-3 py-2.5 font-semibold text-text-primary">
              {locale === "hi" ? "व्रत" : "Vrat"}
            </th>
            <th className="px-3 py-2.5 font-semibold text-text-primary">
              {locale === "hi" ? "अगली तिथि" : "Next date"}
            </th>
            <th className="px-3 py-2.5 font-semibold text-text-primary">
              {locale === "hi" ? "हिंदू मास" : "Hindu month"}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ def, next }) => {
            const href = locale === "hi" ? def.href_hi : def.href_en;
            const label = locale === "hi" ? def.label_hi : def.label_en;
            const sub = locale === "hi" ? def.sub_hi : def.sub_en;
            return (
              <tr key={def.key} className="border-t border-border-subtle">
                <td className="px-3 py-2.5">
                  <Link href={href} className="font-semibold text-intent-brand underline">
                    {label}
                  </Link>
                  <span className="mt-0.5 block text-caption text-text-muted">{sub}</span>
                </td>
                <td className="px-3 py-2.5 text-text-primary">
                  {next ? formatVedicEngineDate(next.date, locale) : "—"}
                </td>
                <td className="px-3 py-2.5 text-text-muted">{next?.month_hi || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
